import { useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

// Wraps the raw URL search-param selectors so any *filter* change also snaps pagination back to page 1;
// pagination itself must keep using useFilterSelectors directly, or every page click would reset itself.
function usePageResettingFilters(pageKey: string) {
  const [searchParams, setSearchParams] = useSearchParams();

  // react-router's setSearchParams updater hands back the render-time snapshot, not the latest state, so a
  // queued/debounced caller would rebuild the URL from stale params and drop filters changed in the meantime.
  const latest = useRef({ searchParams, setSearchParams });
  latest.current = { searchParams, setSearchParams };

  const applyFilters = useCallback(
    (parameterMap: Record<string, string | Array<string>>) => {
      const newValue = new URLSearchParams(latest.current.searchParams.toString());
      for (const [key, value] of Object.entries(parameterMap)) {
        newValue.delete(key);
        if (Array.isArray(value)) {
          value.forEach((v) => newValue.append(key, v));
        } else if (typeof value === 'string' && !!value) {
          newValue.set(key, value);
        }
      }
      newValue.set(pageKey, '1');
      latest.current.setSearchParams(newValue);
    },
    [pageKey]
  );

  const clearFilters = useCallback(
    (...keys: string[]) => {
      const newValue = new URLSearchParams(latest.current.searchParams.toString());
      keys.forEach((key) => newValue.delete(key));
      newValue.set(pageKey, '1');
      latest.current.setSearchParams(newValue);
    },
    [pageKey]
  );

  return { applyFilters, clearFilters };
}

export default usePageResettingFilters;
